import type {RGB} from './color.util';
import {isSameColor} from './color.util';
import chroma from 'chroma-js';

export const sortColorsByPerceptualLightness = (colors: string[]): string[] => {
  return [...colors].sort(compareByCIELAB);
};

export const compareByCIELAB = (colorA: string, colorB: string): number => {
  const [l1, a1, b1] = chroma(colorA).lab();
  const [l2, a2, b2] = chroma(colorB).lab();

  if (l1 !== l2) {
    return l1 - l2;
  }

  if (a1 !== a2) {
    return a1 - a2;
  }

  return b1 - b2;
};

export const getMostContrastingColorFromPalette = (palette: RGB[], referenceLab: [number, number, number], exclude: RGB[]): RGB => {
  let maxDelta = -1;
  let bestColor: RGB | null = null;

  for (const color of palette) {
    // Skip excluded colors
    if (exclude.some((c) => isSameColor(c, color))) {
      continue;
    }

    const lab = rgbToLab(color);
    const d = deltaE(lab, referenceLab);

    if (d > maxDelta) {
      maxDelta = d;
      bestColor = color;
    }
  }

  // Fallback: return any color (e.g. first palette color) if no match found
  return bestColor ?? palette[0];
};

export const findClosestColorInPaletteByLab = (targetColor: RGB, palette: RGB[]): RGB => {
  const targetLab = rgbToLab(targetColor);
  let minDelta = Infinity;
  let closestColor: RGB = targetColor;

  for (const candidate of palette) {
    const candidateLab = rgbToLab(candidate);
    const delta = deltaE(targetLab, candidateLab);

    if (delta < minDelta) {
      minDelta = delta;
      closestColor = candidate;
    }
  }

  return closestColor;
};

export const getAverageLabColor = (colors: RGB[]): [number, number, number] => {
  const total = [0, 0, 0];

  for (const color of colors) {
    const lab = rgbToLab(color);
    total[0] += lab[0];
    total[1] += lab[1];
    total[2] += lab[2];
  }

  const count = colors.length || 1; // prevent divide-by-zero
  return [total[0] / count, total[1] / count, total[2] / count];
};

// Utility: Convert sRGB component to linear RGB
const srgbToLinear = (channel: number): number => {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

// Convert RGBA to XYZ
const rgbToXyz = ([r, g, b]: RGB): [number, number, number] => {
  const R = srgbToLinear(r);
  const G = srgbToLinear(g);
  const B = srgbToLinear(b);

  // Convert to XYZ (D65 illuminant)
  const x = R * 0.4124 + G * 0.3576 + B * 0.1805;
  const y = R * 0.2126 + G * 0.7152 + B * 0.0722;
  const z = R * 0.0193 + G * 0.1192 + B * 0.9505;

  return [x, y, z];
};

// Convert XYZ to LAB
const xyzToLab = ([x, y, z]: [number, number, number]): [number, number, number] => {
  const refX = 0.95047;
  const refY = 1.0;
  const refZ = 1.08883;

  const fx = (t: number): number => {
    return t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116;
  };

  const l = 116 * fx(y / refY) - 16;
  const a = 500 * (fx(x / refX) - fx(y / refY));
  const b = 200 * (fx(y / refY) - fx(z / refZ));

  return [l, a, b];
};

// Final: RGBA to LAB
const rgbToLab = (rgba: RGB): [number, number, number] => {
  const [x, y, z] = rgbToXyz(rgba);
  return xyzToLab([x, y, z]);
};

const deltaE = (lab1: [number, number, number], lab2: [number, number, number]): number => {
  const [L1, a1, b1] = lab1;
  const [L2, a2, b2] = lab2;

  return Math.sqrt((L1 - L2) * (L1 - L2) + (a1 - a2) * (a1 - a2) + (b1 - b2) * (b1 - b2));
};
